import * as React from 'react';
const h = React.createElement;

export interface RadioChangeValue {
  value: string | number | undefined;
}

export interface RadioProps {
  checked?: boolean;
  value?: string | number;
  onChange?: (value: string | number | undefined) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}

export interface RadioOption {
  label: React.ReactNode;
  value: string | number;
}

export interface RadioGroupProps {
  value?: string | number;
  onChange?: (value: string | number | undefined) => void;
  disabled?: boolean;
  children?: React.ReactNode;
  buttonStyle?: 'solid' | 'outline';
  options?: RadioOption[];
}

export interface RadioButtonProps {
  value?: string | number;
  checked?: boolean;
  onChange?: (value: string | number | undefined) => void;
  disabled?: boolean;
  children?: React.ReactNode;
}

interface RadioComponent extends React.FC<RadioProps> {
  Group: React.FC<RadioGroupProps>;
  Button: React.FC<RadioButtonProps>;
}

const RadioBase: React.FC<RadioProps> = function Radio(props) {
  const { checked, value, onChange, disabled, children } = props;
  return h(
    'label',
    { className: 'inline-flex items-center gap-2 cursor-pointer' },
    h('input', {
      type: 'radio',
      checked,
      value,
      disabled,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(e.target.value);
      },
      className: 'text-primary-600 bg-dark-800 border-dark-600 focus:ring-primary-500',
    }),
    children && h('span', { className: 'text-sm text-dark-200' }, children),
  );
};

const Radio = RadioBase as RadioComponent;

Radio.Group = function RadioGroup(props) {
  const { value, onChange, disabled, children, buttonStyle, options } = props;

  if (options) {
    return h(
      'div',
      { className: 'inline-flex flex-wrap gap-2' },
      options.map((opt) => {
        if (buttonStyle === 'solid') {
          return h(
            'label',
            {
              key: opt.value,
              className: `inline-flex items-center px-3 py-1.5 text-sm rounded-lg border cursor-pointer transition-colors ${opt.value === value ? 'bg-primary-600 border-primary-600 text-white' : 'bg-dark-800 border-dark-600 text-dark-300 hover:border-primary-500/50'}`,
            },
            h('input', {
              type: 'radio',
              className: 'sr-only',
              checked: opt.value === value,
              onChange: () => {
                if (onChange) onChange(opt.value);
              },
              disabled,
            }),
            opt.label,
          );
        }
        return h(
          Radio,
          {
            key: opt.value,
            checked: opt.value === value,
            value: opt.value,
            onChange: () => {
              if (onChange) onChange(opt.value);
            },
            disabled,
          },
          opt.label,
        );
      }),
    );
  }

  return h('div', { className: 'inline-flex flex-wrap gap-3' }, children);
};

Radio.Button = function RadioButton(props) {
  const { value, checked, onChange, disabled, children } = props;
  return h(
    'label',
    {
      className: `inline-flex items-center px-3 py-1.5 text-sm rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-primary-600 border-primary-600 text-white' : 'bg-dark-800 border-dark-600 text-dark-300 hover:border-primary-500/50'}`,
    },
    h('input', {
      type: 'radio',
      className: 'sr-only',
      checked,
      value,
      onChange: () => {
        if (onChange) onChange(value);
      },
      disabled,
    }),
    children,
  );
};

export { Radio };
